import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Icon, Modal, message } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { GENRE_OPTIONS, MOOD_OPTIONS } from './constant';
import './index.scss';

interface AlbumFormData {
  albumName: string;
  coverArtPreview: string | null;
  artists: string[];
  genres: string[];
  moods: string[];
  caption: string;
  upc: string;
}

const AlbumCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [isCoverHovered, setIsCoverHovered] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  // 表单数据
  const [formData, setFormData] = useState<AlbumFormData>({
    albumName: '',
    coverArtPreview: null,
    artists: [''],
    genres: [''],
    moods: [''],
    caption: '',
    upc: '',
  });

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.releaseType);
  };

  // 处理封面上传
  const handleCoverClick = () => {
    coverInputRef.current?.click();
  };

  const handleCoverChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        message.error('Please upload a valid image file (JPG or PNG)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error('File size exceeds 10MB limit');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          coverArtPreview: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // 处理艺术家列表
  const handleAddArtist = () => {
    setFormData(prev => ({
      ...prev,
      artists: [...prev.artists, ''],
    }));
  };

  const handleArtistChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      artists: prev.artists.map((a, i) => (i === index ? value : a)),
    }));
  };

  const handleRemoveArtist = (index: number) => {
    if (index > 0) {
      setFormData(prev => ({
        ...prev,
        artists: prev.artists.filter((_, i) => i !== index),
      }));
    }
  };

  // 处理流派列表
  const handleGenreChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.map((g, i) => (i === index ? value : g)),
    }));
  };

  const handleAddGenre = () => {
    setFormData(prev => ({
      ...prev,
      genres: [...prev.genres, ''],
    }));
  };

  const handleRemoveGenre = (index: number) => {
    if (formData.genres.length > 1) {
      setFormData(prev => ({
        ...prev,
        genres: prev.genres.filter((_, i) => i !== index),
      }));
    }
  };

  // 处理情绪列表
  const handleMoodChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      moods: prev.moods.map((m, i) => (i === index ? value : m)),
    }));
  };

  const handleAddMood = () => {
    setFormData(prev => ({
      ...prev,
      moods: [...prev.moods, ''],
    }));
  };

  const handleRemoveMood = (index: number) => {
    if (formData.moods.length > 1) {
      setFormData(prev => ({
        ...prev,
        moods: prev.moods.filter((_, i) => i !== index),
      }));
    }
  };

  // 处理描述变化
  const handleCaptionChange = (value: string) => {
    if (value.length <= 500) {
      setFormData(prev => ({ ...prev, caption: value }));
    }
  };

  // 表单验证
  const isFormValid = () => {
    const validGenres = formData.genres.filter(g => g.trim() !== '');
    return (
      formData.albumName.trim() !== '' &&
      formData.coverArtPreview !== null &&
      formData.artists[0].trim() !== '' &&
      validGenres.length > 0
    );
  };

  // 处理创建
  const handleCreate = () => {
    if (isFormValid()) {
      setShowDialog(true);
    }
  };

  // 处理添加歌曲
  const handleAddSongs = () => {
    // 保存专辑数据到 sessionStorage
    const albumData = {
      albumName: formData.albumName,
      coverArtPreview: formData.coverArtPreview,
      artists: formData.artists,
      genres: formData.genres.filter(g => g.trim() !== ''),
      moods: formData.moods.filter(m => m.trim() !== ''),
      caption: formData.caption,
      upc: formData.upc,
    };
    sessionStorage.setItem('albumData', JSON.stringify(albumData));
    sessionStorage.setItem('releaseType', 'album');
    
    setShowDialog(false);
    // 跳转到上传音频页面
    navigate(ROUTES.releaseUpload);
  };

  // 处理取消
  const handleCancelDialog = () => {
    setShowDialog(false);
    sessionStorage.removeItem('albumData');
    sessionStorage.removeItem('releaseType');
    navigate(ROUTES.dashboard);
  };

  return (
    <div className="album-create">
      {/* 页面标题 */}
      <div className="album-create__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Create Album</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="album-create__content">
        <div className="album-create__card">
          {/* 标题 */}
          <h2 className="album-create__title">Album Information</h2>

          {/* 表单 */}
          <Form form={form} layout="vertical" className="album-create__form">
            {/* 专辑名称 */}
            <Form.Item 
              label="Album Name" 
              required
              className="form-item"
            >
              <Input
                value={formData.albumName}
                onChange={(e) => setFormData(prev => ({ ...prev, albumName: e.target.value }))}
                placeholder="Enter album name"
              />
            </Form.Item>

            {/* 封面上传 */}
            <Form.Item 
              label="Cover Art" 
              required
              className="form-item"
            >
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleCoverChange}
                style={{ display: 'none' }}
              />
              <div
                className={`cover-upload ${isCoverHovered ? 'cover-upload--hover' : ''}`}
                onClick={handleCoverClick}
                onMouseEnter={() => setIsCoverHovered(true)}
                onMouseLeave={() => setIsCoverHovered(false)}
              >
                {formData.coverArtPreview ? (
                  <img src={formData.coverArtPreview} alt="Cover art" className="cover-upload__preview" />
                ) : (
                  <div className="cover-upload__placeholder">
                    <Icon style="UploadBig" />
                  </div>
                )}
              </div>
              <p className="form-item__hint">Min 1000x1000 pixels, JPG or PNG. Maximum 10 MB.</p>
            </Form.Item>

            {/* 艺术家 */}
            <Form.Item 
              label="Artist" 
              required
              className="form-item"
            >
              {formData.artists.map((artist, index) => (
                <div key={index} className="form-item__row">
                  <Input
                    value={artist}
                    onChange={(e) => handleArtistChange(index, e.target.value)}
                    placeholder={index === 0 ? 'Enter artist name' : 'Enter featured artist name'}
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveArtist(index)}
                    >
                      <Icon style="Close" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddArtist}>
                <span>+</span> Add Featured Artist
              </button>
            </Form.Item>

            {/* 流派 */}
            <Form.Item 
              label="Genre" 
              required
              className="form-item"
            >
              {formData.genres.map((genre, index) => (
                <div key={index} className="form-item__row">
                  <Select
                    value={genre || undefined}
                    onChange={(value) => handleGenreChange(index, value as string)}
                    placeholder="Select genre"
                    options={GENRE_OPTIONS.filter(
                      g => !formData.genres.includes(g.value) || g.value === genre
                    )}
                    style={{ width: '100%' }}
                  />
                  {formData.genres.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveGenre(index)}
                    >
                      <Icon style="Close" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddGenre}>
                <span>+</span> Add Another Genre
              </button>
            </Form.Item>

            {/* 情绪 */}
            <Form.Item 
              label="Mood"
              className="form-item"
            >
              {formData.moods.map((mood, index) => (
                <div key={index} className="form-item__row">
                  <Select
                    value={mood || undefined}
                    onChange={(value) => handleMoodChange(index, value as string)}
                    placeholder="Select mood"
                    options={MOOD_OPTIONS.filter(
                      m => !formData.moods.includes(m.value) || m.value === mood
                    )}
                    style={{ width: '100%' }}
                  />
                  {formData.moods.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveMood(index)}
                    >
                      <Icon style="Close" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddMood}>
                <span>+</span> Add Another Mood
              </button>
            </Form.Item>

            {/* 描述 */}
            <Form.Item 
              label={
                <div className="form-label-with-counter">
                  <span>Caption</span>
                  <span className="counter">{formData.caption.length}/500</span>
                </div>
              }
              className="form-item form-item--full"
            >
              <Input.TextArea
                value={formData.caption}
                onChange={(e) => handleCaptionChange(e.target.value)}
                placeholder="Add a description or caption for your album..."
                rows={4}
              />
            </Form.Item>

            {/* UPC */}
            <Form.Item 
              label="UPC"
              className="form-item"
            >
              <Input
                value={formData.upc}
                onChange={(e) => setFormData(prev => ({ ...prev, upc: e.target.value }))}
                placeholder="Universal Product Code"
              />
            </Form.Item>

            {/* 创建按钮 */}
            <Button
              type="primary"
              disabled={!isFormValid()}
              onClick={handleCreate}
              className={`album-create__submit ${!isFormValid() ? 'album-create__submit--disabled' : ''}`}
            >
              Create
            </Button>
          </Form>
        </div>
      </div>

      {/* 创建成功对话框 */}
      <Modal
        open={showDialog}
        onCancel={() => setShowDialog(false)}
        footer={null}
        centered
        closable={false}
        className="album-created-dialog"
        width={480}
      >
        <div className="dialog-content">
          <div className="dialog-icon">
            <Icon style="Album" size="large" />
          </div>
          <h2 className="dialog-title">Album Created!</h2>
          <p className="dialog-subtitle">
            Your album "{formData.albumName}" has been created successfully.
          </p>
          <p className="dialog-desc">Would you like to add songs to your album now?</p>
          
          <div className="dialog-actions">
            <Button type="primary" onClick={handleAddSongs} className="dialog-btn dialog-btn--primary">
              Add Songs
            </Button>
            <Button onClick={handleCancelDialog} className="dialog-btn dialog-btn--secondary">
              Later
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AlbumCreate;
